import {
  computed,
  EventEmitter,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ApiService } from './api.service';
import { base64ToBlob } from 'base64-blob';
import { Base64Helper } from '../utils/base64-helper';
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet';
import {
  Camera,
  CameraResultType,
  CameraSource,
  Photo,
} from '@capacitor/camera';
import { Filesystem } from '@capacitor/filesystem';

@Injectable({ providedIn: 'root' })
export class UploadService {
  private apiService = inject(ApiService);
  private translateService = inject(TranslateService);

  previewUpdated = new EventEmitter<Photo>();

  private readonly pendingUploads = signal(0);
  public readonly uploading = computed(() => this.pendingUploads() > 0);

  public selectUpload(): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      void (async () => {
        try {
          const actionSheetOptions = {
            title: this.translateService.instant('camera.info'),
            options: [
              { title: this.translateService.instant('camera.take') },
              { title: this.translateService.instant('camera.select') },
              { title: this.translateService.instant('camera.document') },
              {
                title: this.translateService.instant('camera.cancel'),
                style: ActionSheetButtonStyle.Cancel,
              },
            ],
          };

          const value = await ActionSheet.showActions(actionSheetOptions);

          if (value.index === 3) {
            reject(new Error('cancelled'));
            return;
          }

          if (value.index === 2) {
            resolve('document');
            return;
          }

          const options = {
            quality: 60,
            resultType: CameraResultType.Uri,
            correctOrientation: true,
            source:
              value.index === 0 ? CameraSource.Camera : CameraSource.Photos,
          };

          const image = await Camera.getPhoto(options);
          this.previewUpdated.next(image);
          resolve(image.path);
        } catch (error) {
          reject(error);
        }
      })();
    });
  }

  public upload(type: string, file: string | File): Promise<{ file: string }> {
    return new Promise<{ file: string }>((resolve, reject) => {
      void (async () => {
        this.pendingUploads.update((count) => count + 1);
        try {
          let blob: Blob;

          if (typeof file === 'string') {
            const fileResult = await Filesystem.readFile({ path: file });
            const data = Base64Helper.formatBase64JpegImage(fileResult.data);
            blob = await base64ToBlob(data);
          } else {
            blob = file as File;
          }

          const formData = new FormData();
          formData.append('file', blob);

          const response = await this.apiService
            .identifiedPost(`/api/v2/uploads/${type}`, formData)
            .toPromise();

          resolve(response);
        } catch (error) {
          reject(error);
        } finally {
          this.pendingUploads.update((count) => count - 1);
        }
      })();
    });
  }
}
